import { useContext } from 'react';
import { Button } from '@shared/components/Button';
import { Icon, IconName } from '@shared/components/Icon';
import { Image, ImageSource } from '@shared/components/Image';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { ColorThemeContext, grid } from '@shared/styles';
import { GRID_UNIT } from '@shared/styles/constants';
import classNames from './emptyListPlaceholder.module.css';

export interface MBEmptyListPlaceholderProps {
  title?: string;
  description?: string;
  image?: ImageSource;
  /** A line-art icon instead of an `image`, tinted and sized like one. */
  icon?: IconName;
  imageTintColor?: string;
  imageSize?: number;
  imagePosition?: 'above' | 'below';
  imageVerticalGap?: number;
  buttonTitle?: string;
  onButtonPress?(): void;
  testID?: string;
  className?: string;
}

export function MBEmptyListPlaceholder({
  title,
  description,
  image,
  icon,
  imageTintColor,
  imageSize = grid(24),
  imagePosition = 'above',
  imageVerticalGap = 2,
  buttonTitle,
  onButtonPress,
  testID,
  className,
}: MBEmptyListPlaceholderProps) {
  const colors = useContext(ColorThemeContext);

  const tintColor = imageTintColor !== undefined ? imageTintColor : colors.highlightActive;
  const imageElement = getImageElement({ image, icon, tintColor, imageSize });
  const hasImage = !!imageElement;

  return (
    <View fill center padding={2} testID={testID} className={className}>
      {imagePosition === 'above' && imageElement}
      {imagePosition === 'above' && hasImage && <View paddingTop={imageVerticalGap} />}
      {!!title && (
        <Text size="h4" align="center" balance style={styles.title}>
          {title}
        </Text>
      )}
      <View paddingTop={2} />
      {!!description && (
        <Text size="body1" inactive align="center" balance style={styles.description}>
          {description}
        </Text>
      )}
      {imagePosition === 'below' && hasImage && <View paddingTop={imageVerticalGap} />}
      {imagePosition === 'below' && imageElement}
      <View paddingTop={2} />
      {!!buttonTitle && !!onButtonPress && (
        <Button variant="secondary" size="medium" label={buttonTitle} onClick={onButtonPress} />
      )}
    </View>
  );
}

function getImageElement({
  image,
  icon,
  tintColor,
  imageSize,
}: {
  image: ImageSource | undefined;
  icon: IconName | undefined;
  tintColor: string;
  imageSize: number;
}) {
  if (icon) {
    return <Icon name={icon} color={tintColor} gridSize={imageSize / GRID_UNIT} />;
  }
  if (image) {
    return (
      <Image
        source={image}
        tintColor={tintColor}
        className={classNames.image}
        style={{ height: imageSize }}
        resizeMode="contain"
      />
    );
  }
  return undefined;
}

const styles = {
  title: {
    maxWidth: grid(60),
  },
  description: {
    maxWidth: grid(60),
  },
};
